import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { DollarSign, Repeat, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/affiliates")({
  component: AffiliatesLanding,
  head: () => ({
    meta: [
      { title: "Inquira Creator Affiliate Program — Earn 15% Recurring" },
      {
        name: "description",
        content:
          "Apply to the Inquira creator program and earn 15% recurring commission on Premium subscriptions you refer. Monthly payouts once your balance reaches $25.",
      },
      { property: "og:title", content: "Earn 15% recurring with Inquira" },
      {
        property: "og:description",
        content: "Creators earn 15% recurring commission on every Inquira Premium subscriber they refer.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/affiliates" }],
  }),
});

const TERMS_VERSION = 1;

function AffiliatesLanding() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState<{ status: string } | null>(null);
  const [checking, setChecking] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    full_name: "",
    creator_name: "",
    contact_email: "",
    website: "",
    primary_platform: "tiktok",
    audience_type: "students",
    follower_count: "",
    estimated_monthly_reach: "",
    country: "",
    payment_country: "",
    payout_method: "stripe_connect",
    payout_email: "",
    promotion_plan: "",
    previous_experience: "",
  });
  const [tax, setTax] = useState({
    tax_legal_name: "",
    tax_entity_type: "individual",
    tax_country: "",
    tax_form_type: "w9",
    tax_id_last4: "",
    verification_method: "government_id",
  });
  const [terms, setTerms] = useState(false);
  const [disclosure, setDisclosure] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const setTaxField = (k: keyof typeof tax) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setTax((t) => ({ ...t, [k]: e.target.value }));

  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      setChecking(false);
      return;
    }
    setForm((f) => ({ ...f, contact_email: f.contact_email || (user.email ?? ""), payout_email: f.payout_email || (user.email ?? "") }));
    (async () => {
      const { data } = await supabase.rpc("my_affiliate_summary" as never);
      const res = data as { affiliate?: { status: string } | null } | null;
      if (res?.affiliate) setExisting({ status: res.affiliate.status });
      setChecking(false);
    })();
  }, [user, isLoading]);

  async function submit() {
    if (!user) return void navigate({ to: "/login" });
    if (form.creator_name.trim().length < 2) return toast.error("Tell us your creator or channel name");
    if (!form.contact_email.includes("@")) return toast.error("Enter a valid contact email");
    if (!form.payout_email.includes("@")) return toast.error("Enter a valid payout email");
    if (form.promotion_plan.trim().length < 20) return toast.error("Tell us a bit more about how you'll promote Inquira");
    if (tax.tax_legal_name.trim().length < 2) return toast.error("Enter the legal name that should appear on your tax form");
    if (tax.tax_country.trim().length < 2) return toast.error("Enter your tax residence country");
    if (tax.tax_id_last4.replace(/[^0-9A-Za-z]/g, "").length < 4)
      return toast.error("Enter the last 4 digits of your tax ID");
    if (!terms || !disclosure) return toast.error("Please accept the affiliate terms and the disclosure requirement");

    setSubmitting(true);
    const { data, error } = await supabase.rpc("submit_affiliate_application" as never, {
      _payload: {
        ...form,
        social_links: form.website ? [form.website] : [],
        terms_accepted: "true",
        disclosure_accepted: "true",
        terms_version: TERMS_VERSION,
        user_agent: typeof navigator === "undefined" ? "" : navigator.userAgent,
      },
    } as never);
    setSubmitting(false);
    if (error) return toast.error(error.message);
    const out = data as { ok: boolean; error?: string } | null;
    if (!out?.ok) {
      if (out?.error === "already_applied") return void navigate({ to: "/affiliate" });
      return toast.error(out?.error === "terms_required" ? "Please accept the terms" : (out?.error ?? "Failed to submit"));
    }
    toast.success("Application submitted — we review within 2 business days");
    // Compliance details are stored separately so they stay reviewable and updatable later.
    const { data: comp } = await supabase.rpc("update_my_affiliate_compliance" as never, {
      _payload: { ...tax, tax_country: tax.tax_country.trim().toUpperCase() },
    } as never);
    if (!(comp as { ok?: boolean } | null)?.ok) {
      toast.info("Add your tax and identity details from the dashboard to unlock payouts.");
    }
    void navigate({ to: "/affiliate" });
  }

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <h1 className="text-3xl font-bold sm:text-4xl">
        Earn <span className="text-neon-pink">15% recurring</span> with Inquira
      </h1>
      <p className="mt-3 text-muted-foreground">
        Share the AI study companion your audience already needs. Approved creators get a tracked link, a coupon code,
        marketing assets, and a live earnings dashboard.
      </p>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        {[
          { Icon: Repeat, title: "15% recurring", body: "Paid every month a referred subscriber stays Premium." },
          { Icon: DollarSign, title: "Monthly payouts", body: "Issued monthly once your balance reaches $25." },
          { Icon: ShieldCheck, title: "90-day attribution", body: "First-touch tracking with a 90-day window." },
        ].map(({ Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-border bg-card p-4">
            <Icon className="h-4 w-4 text-neon-pink" />
            <div className="mt-2 font-semibold">{title}</div>
            <p className="text-sm text-muted-foreground">{body}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        Payouts are issued monthly once your available commission balance reaches $25. Balances below $25 automatically
        carry forward to the next payout period.
      </p>

      <h2 className="mt-8 text-xl font-semibold">Apply to the program</h2>

      {checking && user ? (
        <p className="mt-3 text-sm text-muted-foreground">Checking your application…</p>
      ) : existing ? (
        <div className="mt-3 rounded-2xl border border-border bg-card p-5 text-sm">
          You already have an affiliate profile (status: <strong className="capitalize">{existing.status}</strong>).{" "}
          <Link to="/affiliate" className="text-primary underline">
            Open your creator dashboard
          </Link>
          .
        </div>
      ) : (
        <div className="mt-3 space-y-3 rounded-2xl border border-border bg-card p-5">
          {!user && (
            <p className="rounded-lg border border-border p-3 text-sm text-muted-foreground">
              You can fill this in now —{" "}
              <Link to="/login" className="text-primary underline">
                sign in
              </Link>{" "}
              before submitting so we can attach it to your account.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Full name">
              <input value={form.full_name} onChange={set("full_name")} className={inputCls} />
            </Field>
            <Field label="Creator / channel name">
              <input value={form.creator_name} onChange={set("creator_name")} className={inputCls} placeholder="Jane Studies" />
            </Field>
            <Field label="Contact email">
              <input value={form.contact_email} onChange={set("contact_email")} type="email" className={inputCls} />
            </Field>
            <Field label="Website or main channel URL">
              <input value={form.website} onChange={set("website")} className={inputCls} placeholder="https://tiktok.com/@…" />
            </Field>
            <Field label="Primary platform">
              <select value={form.primary_platform} onChange={set("primary_platform")} className={inputCls}>
                {["tiktok", "instagram", "youtube", "x", "twitch", "discord", "blog", "newsletter", "other"].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Audience type">
              <select value={form.audience_type} onChange={set("audience_type")} className={inputCls}>
                {["students", "parents", "teachers", "tutors", "mixed"].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Follower count">
              <input value={form.follower_count} onChange={set("follower_count")} inputMode="numeric" className={inputCls} />
            </Field>
            <Field label="Estimated monthly reach">
              <input
                value={form.estimated_monthly_reach}
                onChange={set("estimated_monthly_reach")}
                inputMode="numeric"
                className={inputCls}
              />
            </Field>
            <Field label="Country">
              <input value={form.country} onChange={set("country")} className={inputCls} placeholder="US" />
            </Field>
            <Field label="Payment country">
              <input value={form.payment_country} onChange={set("payment_country")} className={inputCls} placeholder="US" />
            </Field>
            <Field label="Preferred payout method">
              <select value={form.payout_method} onChange={set("payout_method")} className={inputCls}>
                <option value="stripe_connect">Stripe Connect (preferred)</option>
                <option value="paypal">PayPal</option>
                <option value="bank_transfer">Bank transfer</option>
                <option value="wise">Wise</option>
              </select>
            </Field>
            <Field label="Payout email">
              <input value={form.payout_email} onChange={set("payout_email")} type="email" className={inputCls} />
            </Field>
            <Field label="How will you promote Inquira?" wide>
              <textarea
                value={form.promotion_plan}
                onChange={set("promotion_plan")}
                rows={3}
                className={inputCls}
                placeholder="Weekly homework-help videos on TikTok, link in bio, plus a study newsletter…"
              />
            </Field>
            <Field label="Previous affiliate experience (optional)" wide>
              <textarea value={form.previous_experience} onChange={set("previous_experience")} rows={2} className={inputCls} />
            </Field>
          </div>

          <div className="rounded-xl border border-border p-4">
            <h3 className="text-sm font-semibold">Tax information &amp; identity verification</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Required before your first payout. We store only your entity details and the last 4 digits of your tax ID —
              never a full tax number. Statuses move from required → pending → approved as our team reviews them.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Legal name (as on tax form)">
                <input value={tax.tax_legal_name} onChange={setTaxField("tax_legal_name")} className={inputCls} />
              </Field>
              <Field label="Entity type">
                <select value={tax.tax_entity_type} onChange={setTaxField("tax_entity_type")} className={inputCls}>
                  <option value="individual">Individual / sole proprietor</option>
                  <option value="company">Company</option>
                  <option value="partnership">Partnership</option>
                  <option value="nonprofit">Non-profit</option>
                </select>
              </Field>
              <Field label="Tax residence country">
                <input value={tax.tax_country} onChange={setTaxField("tax_country")} className={inputCls} placeholder="US" />
              </Field>
              <Field label="Tax form">
                <select value={tax.tax_form_type} onChange={setTaxField("tax_form_type")} className={inputCls}>
                  <option value="w9">W-9 (US person)</option>
                  <option value="w8ben">W-8BEN (non-US individual)</option>
                  <option value="w8bene">W-8BEN-E (non-US entity)</option>
                </select>
              </Field>
              <Field label="Last 4 of tax ID (SSN / EIN / VAT)">
                <input
                  value={tax.tax_id_last4}
                  onChange={setTaxField("tax_id_last4")}
                  maxLength={4}
                  inputMode="numeric"
                  className={inputCls}
                  placeholder="1234"
                />
              </Field>
              <Field label="Identity verification method">
                <select value={tax.verification_method} onChange={setTaxField("verification_method")} className={inputCls}>
                  <option value="government_id">Government ID</option>
                  <option value="stripe_identity">Stripe Identity</option>
                  <option value="business_registration">Business registration</option>
                </select>
              </Field>
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1" />
            <span>
              I accept the affiliate terms: no self-referrals, no paid brand-name bidding, no coupon-site spam, and
              commissions may be reversed on refunds or chargebacks. See the{" "}
              <Link to="/terms" className="text-primary underline">
                Terms
              </Link>
              .
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={disclosure} onChange={(e) => setDisclosure(e.target.checked)} className="mt-1" />
            <span>I will clearly disclose my affiliate relationship wherever I promote Inquira.</span>
          </label>

          <button
            disabled={submitting}
            onClick={submit}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" /> {submitting ? "Submitting…" : "Submit application"}
          </button>
        </div>
      )}
    </div>
  );
}

const inputCls = "mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm";

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <span className="text-sm">{label}</span>
      {children}
    </label>
  );
}